import { FormattedMessage, useIntl } from "react-intl";
import { Control, DeepRequired, FieldErrorsImpl, UseFormClearErrors, UseFormSetValue, UseFormWatch } from "react-hook-form";

import Checkbox from "components/atoms/Checkbox/Checkbox";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import Input from "components/atoms/Input/Input";
import useAddressV2Fields from "hooks/useAddressV2Fields";
import usePermissions from "hooks/usePermissions";
import { LENGTH_OPTIONS, WEIGHT_OPTIONS, DEFAULT_PAYMENT_LOCATION_VALUES_TYPE } from "../constants";
import usePaymentLocationQueries from "./usePaymentLocationQueries";
import useAddressV1Fields from "./useAddressV1Fields";

interface usePaymentLocationFieldsProps {
  viewOnly?: boolean;
  getIntercomOptions: () => Array<{ value: string; label: string }>;
  getDefaultIncotermOptions: () => Array<{ value: string; label: string }>;
  watch: UseFormWatch<DEFAULT_PAYMENT_LOCATION_VALUES_TYPE>;
  errors: FieldErrorsImpl<DeepRequired<DEFAULT_PAYMENT_LOCATION_VALUES_TYPE>>;
  control: Control<DEFAULT_PAYMENT_LOCATION_VALUES_TYPE>;
  setValue: UseFormSetValue<DEFAULT_PAYMENT_LOCATION_VALUES_TYPE>;
  clearErrors: UseFormClearErrors<DEFAULT_PAYMENT_LOCATION_VALUES_TYPE>;
}

export default function usePaymentLocationFields({
  viewOnly,
  getIntercomOptions,
  getDefaultIncotermOptions,
  watch,
  errors,
  control,
  setValue,
  clearErrors,
}: usePaymentLocationFieldsProps) {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const { enable_new_address_structure } = JSON.parse(permissions || "{}")?.company_shipper_settings || {};

  const freightModeError = errors?.freight_modes as any;

  const { currenciesData, freightModesData } = usePaymentLocationQueries();

  const { fields: addressV2Fields } = useAddressV2Fields({
    viewOnly,
    control,
    watch,
    setValue,
    enable_new_address_structure,
  });
  const { fields: addressV1Fields } = useAddressV1Fields({ viewOnly });

  const countryField = enable_new_address_structure ? addressV2Fields.slice(0, 1) : addressV1Fields.slice(0, 1);
  const stateCityAndZipcodeField = enable_new_address_structure ? addressV2Fields.slice(1) : addressV1Fields.slice(1);

  const renderPaymentLocationName = () => (
    <Input
      type="text"
      required
      name="location_name"
      placeholder={intl.formatMessage({
        id: "settings.payment-locations.paymentLocationNamePlaceholder",
        defaultMessage: "Team's name",
      })}
      className="rounded-input input-width-lg"
      disabled={viewOnly}
    />
  );

  const renderIncoterm = () => (
    <>
      <div className="mb-3 w-192-px">
        <Dropdown
          required
          useHookForm
          name="incoterm_enabled"
          options={getIntercomOptions()}
          placeholder={intl.formatMessage({ id: "general.pleaseSelect", defaultMessage: "Please select" })}
          disabled={viewOnly}
        />
      </div>

      <div className="mb-3 w-192-px">
        <div className="mb-2">
          <FormattedMessage id="settings.payment-locations.defaultValue" defaultMessage="Default value" />
        </div>
        <Dropdown
          useHookForm
          placeholder={intl.formatMessage({ id: "general.pleaseSelect", defaultMessage: "Please select" })}
          name="incoterm_default_value"
          options={getDefaultIncotermOptions()}
          disabled={viewOnly}
        />
      </div>

      <Checkbox
        checkboxLabel={intl.formatMessage({
          id: "settings.payment-locations.incotermVisible",
          defaultMessage: "Incoterm visible",
        })}
        name="incoterm_visible"
        required={false}
        providerSections
        checked={watch("incoterm_visible") || false}
        disabled={viewOnly}
      />
    </>
  );

  const renderInvoiceAddressee = () => (
    <Input
      type="text"
      required
      name="addressee"
      className="rounded-input input-width-lg"
      placeholder={intl.formatMessage({
        id: "settings.payment-locations.invoicesAddressedToPlaceholder",
        defaultMessage: "Invoices will be addressed to...",
      })}
      disabled={viewOnly}
    />
  );

  const renderAddresseePhone = () => (
    <Input
      placeholder={intl.formatMessage({
        id: "settings.payment-locations.addresseePhonePlaceholder",
        defaultMessage: "The contact person's international phone number",
      })}
      type="text"
      required
      name="phone"
      className="rounded-input input-width-lg"
      disabled={viewOnly}
    />
  );

  const renderInvoiceAddress = () => (
    <Input
      placeholder={intl.formatMessage({
        id: "settings.payment-locations.invoiceAddressPlaceholder",
        defaultMessage: "Invoice will be sent to this address",
      })}
      type="text"
      required
      name="address"
      className="rounded-input input-width-lg"
      disabled={viewOnly}
    />
  );

  const renderSendApprovedInvoiceTo = () => (
    <Input
      disabled={viewOnly}
      type="text"
      name="approved_invoice_emails"
      className="rounded-input input-width-lg"
      placeholder={intl.formatMessage({
        id: "settings.payment-locations.SendApprovedInvoiceToPlaceholder",
        defaultMessage: "Comma-separated emails",
      })}
    />
  );

  const renderInvoicingCurrency = () => (
    <div className="">
      <Dropdown
        required
        useHookForm
        name="currency_code"
        options={currenciesData || []}
        placeholder={intl.formatMessage({ id: "general.pleaseSelect", defaultMessage: "Please select" })}
        disabled={viewOnly}
      />
    </div>
  );

  const renderUnitSystemDefault = () => (
    <div className="d-flex">
      <div className="w-90-px">
        <Dropdown
          useHookForm
          name="weight_preference"
          disabled={viewOnly}
          placeholder={intl.formatMessage({ id: "general.select", defaultMessage: "Select" })}
          options={WEIGHT_OPTIONS}
        />
      </div>
      <div className="ml-3 w-90-px">
        <Dropdown
          useHookForm
          name="length_preference"
          disabled={viewOnly}
          placeholder={intl.formatMessage({ id: "general.select", defaultMessage: "Select" })}
          options={LENGTH_OPTIONS}
        />
      </div>
    </div>
  );

  const renderFreightModes = () => (
    <>
      {freightModeError && (
        <div className="text-danger font-12 mb-2">
          <span className="mr-2">Please select at least 1 freight mode</span>
        </div>
      )}
      {freightModesData &&
        freightModesData?.length > 0 &&
        freightModesData.map((freightMode: any) => (
          <div key={freightMode.value} className="mb-2">
            <Checkbox
              checkboxLabel={freightMode.label}
              name={`freight_modes.${freightMode.value}`}
              required={false}
              providerSections
              disabled={viewOnly}
              customOnChange={() => clearErrors("freight_modes")}
            />
          </div>
        ))}
    </>
  );

  const invoiceDetailFields = [
    {
      label: <FormattedMessage id="general.name" defaultMessage="Name" />,
      children: renderInvoiceAddressee(),
      required: true,
    },
    {
      label: <FormattedMessage id="settings.contact-list.phone.placeholder" defaultMessage="Phone number" />,
      children: renderAddresseePhone(),
      required: true,
    },
    {
      label: (
        <FormattedMessage id="settings.payment-locations.sendApprovedInvoiceTo" defaultMessage="Send approved invoice to" />
      ),
      children: renderSendApprovedInvoiceTo(),
    },
    {
      label: <FormattedMessage id="settings.payment-locations.invoicingCurrency" defaultMessage="Invoicing currency" />,
      children: renderInvoicingCurrency(),
      required: true,
    },
    ...countryField,
    {
      label: <FormattedMessage id="general.address" defaultMessage="Address" />,
      children: renderInvoiceAddress(),
      required: true,
    },
    ...stateCityAndZipcodeField,
  ];

  const paymentLocationFields = [
    {
      label: <FormattedMessage id="settings.payment-locations.paymentLocationName" defaultMessage="Payment location name" />,
      children: renderPaymentLocationName(),
      required: true,
    },
    {
      label: <FormattedMessage id="general.incoterm" defaultMessage="Incoterm" />,
      children: renderIncoterm(),
      required: true,
    },

    {
      label: <FormattedMessage id="settings.payment-locations.unitSystemDefault" defaultMessage="Unit system default" />,
      children: renderUnitSystemDefault(),
      required: true,
    },
    {
      label: <FormattedMessage id="general.freightModes" defaultMessage="Freight modes" />,
      children: renderFreightModes(),
      required: true,
    },
  ];

  return { invoiceDetailFields, paymentLocationFields };
}
